import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Collapse,
  Badge,
  Spinner,
  Alert,
  Toast,
  ToastBody,
  ToastHeader,
} from "reactstrap";
import Document from "../components/contract/Document";
import DocumentHistoryTable from "../components/contract/DocumentHistoryTable";
import EsignService from "../../../service/EsignService";
import { useAuth } from "../../../contexts/AuthContext";
import dealService from "../../../service/dealService.ts";

const ContractTab = ({ deal }) => {
  const { getToken } = useAuth();

  // Accordion states for Contract tab
  const [accordionOpen, setAccordionOpen] = useState({
    currentAddendum: true,
    addendumHistory: false,
    currentContract: false,
    contractHistory: false,
  });

  // State for contract data from EsignService
  const [contractData, setContractData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for contract history data
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  // State for addendum data from EsignService
  const [addendumData, setAddendumData] = useState(null);
  const [addendumLoading, setAddendumLoading] = useState(false);
  const [addendumError, setAddendumError] = useState(null);

  // State for addendum history data
  const [addendumHistoryData, setAddendumHistoryData] = useState(null);
  const [addendumHistoryLoading, setAddendumHistoryLoading] = useState(false);
  const [addendumHistoryError, setAddendumHistoryError] = useState(null);

  // State for addendum preference (whether user wants addendum or not)
  const [addendumPreference, setAddendumPreference] = useState(null);
  const [addendumPreferenceLoading, setAddendumPreferenceLoading] =
    useState(false);
  const [addendumPreferenceError, setAddendumPreferenceError] = useState(null);

  // State for toast notifications
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // State to track the actual displayed status from the UI
  const [displayedAddendumStatus, setDisplayedAddendumStatus] = useState(null);

  // Helper function to check if user wants an addendum
  const userWantsAddendum = () => {
    return addendumPreference?.has_addendum === true;
  };

  // Helper function to check if addendum is completed (using displayed status from API)
  const isAddendumCompleted = () => {
    if (!userWantsAddendum()) return true; // If no addendum wanted, consider it "completed"
    if (!displayedAddendumStatus) return false;
    
    // For addendum flow: use the displayed status from the API response
    return displayedAddendumStatus.toLowerCase() === "completed";
  };

  // Helper function to get contract status from API response
  const getContractStatus = () => {
    return contractData?.status || null;
  };

  // Helper function to get addendum status from API response
  const getAddendumStatus = () => {
    return addendumData?.status || displayedAddendumStatus || null;
  };


  // Toggle accordion sections with conditional logic for contract sections
  const toggleAccordion = (section) => {
    // If trying to access contract sections but addendum is not completed, prevent toggle
    if (
      (section === "currentContract" || section === "contractHistory") &&
      !isAddendumCompleted()
    ) {
      return;
    }

    setAccordionOpen((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Helper function to format dates
  const formatDateTime = (dateTime) => {
    if (!dateTime) return "N/A";
    try {
      return new Date(dateTime).toLocaleString();
    } catch (err) {
      return dateTime;
    }
  };

  // Function to get contract data from EsignService
  const getContract = async (deal) => {
    console.log("getContract called with deal:", deal);

    if (!deal?.publicId) {
      console.warn("No deal publicId available for contract lookup");
      return;
    }

    console.log("Deal information: ", deal);
    setLoading(true);
    setError(null);

    try {
      // Get token using the useAuth hook (same pattern as PreApprovedTab)
      const token = getToken();

      if (!token) {
        throw new Error("No authentication token available");
      }

      // Call the EsignService method with CONTRACT document type
      console.log(
        "About to call EsignService.getContractByReferenceId with token:",
        token ? "present" : "missing",
        "publicId:",
        deal.publicId
      );
      const response = await EsignService.getContractByReferenceId(
        token,
        deal.publicId,
        "CONTRACT"
      );
      console.log("EsignService call completed, response:", response);

      // Save the result in the contractData state
      setContractData(response.data);

      console.log("Contract data retrieved:", response.data);
      console.log("Contract status from API:", response.data?.status);

      // If we have envelope ID in the response, fetch history data
      if (response.data?.envelopeId) {
        await getHistory(response.data.envelopeId, "contract");
      }
    } catch (err) {
      console.error("Error fetching contract data:", err);
      setError(err.message || "Failed to fetch contract data");
    } finally {
      setLoading(false);
    }
  };

  // Function to get addendum preference from dealService
  const getAddendumPreference = async (deal) => {
    console.log("getAddendumPreference called with deal:", deal);

    if (!deal?.privateId) {
      console.warn(
        "No deal privateId available for addendum preference lookup"
      );
      return;
    }

    setAddendumPreferenceLoading(true);
    setAddendumPreferenceError(null);

    try {
      const token = getToken();

      if (!token) {
        throw new Error("No authentication token available");
      }

      console.log(
        "Calling dealService.getDealAddendumByDealId with privateId:",
        deal.privateId
      );
      const response = await dealService.getDealAddendumByDealId(
        token,
        deal.privateId
      );
      console.log("Addendum preference response:", response);

      setAddendumPreference(response.data);
      console.log("Addendum preference set:", response.data);
    } catch (err) {
      console.error("Error fetching addendum preference:", err);
      // If we get a 404, it means no addendum preference exists (user doesn't want addendum)
      if (err.response?.status === 404) {
        console.log(
          "No addendum preference found (404), user doesn't want addendum"
        );
        setAddendumPreference({ has_addendum: false });
      } else {
        setAddendumPreferenceError(
          err.message || "Failed to fetch addendum preference"
        );
      }
    } finally {
      setAddendumPreferenceLoading(false);
    }
  };

  // Function to get addendum data from EsignService
  const getAddendum = async (deal) => {
    console.log("getAddendum called with deal:", deal);

    if (!deal?.publicId) {
      console.warn("No deal publicId available for addendum lookup");
      return;
    }

    console.log("Deal information for addendum: ", deal);
    setAddendumLoading(true);
    setAddendumError(null);

    try {
      // Get token using the useAuth hook
      const token = getToken();

      if (!token) {
        throw new Error("No authentication token available");
      }

      // Call the EsignService method with ADDENDUM document type
      console.log(
        "About to call EsignService.getContractByReferenceId for addendum with token:",
        token ? "present" : "missing",
        "publicId:",
        deal.publicId
      );
      const response = await EsignService.getContractByReferenceId(
        token,
        deal.publicId,
        "ADDENDUM"
      );
      console.log("EsignService addendum call completed, response:", response);

      // Save the result in the addendumData state
      setAddendumData(response.data);

      console.log("Addendum data retrieved:", response.data);
      console.log("Addendum status from API:", response.data?.status);
      
      // Set the displayed addendum status directly from the API response
      if (response.data?.status) {
        setDisplayedAddendumStatus(response.data.status);
      }

      // If we have envelope ID in the response, fetch addendum history data
      if (response.data?.envelopeId) {
        await getHistory(response.data.envelopeId, "addendum");
      }
    } catch (err) {
      console.error("Error fetching addendum data:", err);
      setAddendumError(err.message || "Failed to fetch addendum data");
    } finally {
      setAddendumLoading(false);
    }
  };

  // Function to get webhook history from EsignService
  const getHistory = async (envelopeId, documentType = "contract") => {
    if (!envelopeId) {
      console.warn("No envelope ID available for webhook history lookup");
      return;
    }

    const isAddendum = documentType === "addendum";

    if (isAddendum) {
      setAddendumHistoryLoading(true);
      setAddendumHistoryError(null);
    } else {
      setHistoryLoading(true);
      setHistoryError(null);
    }

    try {
      // Get token using the useAuth hook
      const token = getToken();

      if (!token) {
        throw new Error("No authentication token available");
      }

      // Call the EsignService getWebhookHistory method (webhook endpoint)
      const response = await EsignService.getWebhookHistory(token, envelopeId);

      // Save the result in the appropriate state based on document type
      if (isAddendum) {
        setAddendumHistoryData(response.data);
        console.log("Addendum webhook history retrieved:", response.data);
        console.log("Is array?", Array.isArray(response.data));
        console.log("Length:", Array.isArray(response.data) ? response.data.length : 'Not an array');
      } else {
        setHistoryData(response.data);
        console.log("Contract webhook history retrieved:", response.data);
        console.log("Is array?", Array.isArray(response.data));
        console.log("Length:", Array.isArray(response.data) ? response.data.length : 'Not an array');
      }
    } catch (err) {
      console.error(`Error fetching ${documentType} webhook history:`, err);
      const errorMessage =
        err.message || `Failed to fetch ${documentType} webhook history`;

      if (isAddendum) {
        setAddendumHistoryError(errorMessage);
      } else {
        setHistoryError(errorMessage);
      }
    } finally {
      if (isAddendum) {
        setAddendumHistoryLoading(false);
      } else {
        setHistoryLoading(false);
      }
    }
  };

  // Effect to load addendum preference when component mounts or deal changes
  useEffect(() => {
    console.log("ContractTab useEffect triggered, deal:", deal);
    if (deal) {
      console.log("Deal exists, calling getAddendumPreference");
      getAddendumPreference(deal);
    } else {
      console.log("No deal provided to ContractTab");
    }
  }, [deal]);

  // Effect to load addendum data when preference is loaded and user wants addendum
  useEffect(() => {
    if (deal && addendumPreference && userWantsAddendum()) {
      console.log("User wants addendum, loading addendum data");
      getAddendum(deal);
    } else if (addendumPreference && !userWantsAddendum()) {
      console.log("User doesn't want addendum, clearing addendum data");
      setAddendumData(null);
      setAddendumHistoryData(null);
    }
  }, [deal, addendumPreference]);

  // Effect to load contract data based on addendum preference and completion status
  useEffect(() => {
    if (deal && addendumPreference) {
      if (!userWantsAddendum()) {
        // Case 2: User doesn't want addendum, load contract immediately
        console.log(
          "User doesn't want addendum, loading contract data immediately"
        );
        getContract(deal);
      } else if (userWantsAddendum() && isAddendumCompleted()) {
        // Case 3: User wants addendum and it's completed, load contract
        console.log("Addendum is completed, loading contract data");
        getContract(deal);
      } else if (userWantsAddendum() && !isAddendumCompleted()) {
        // Case 1: User wants addendum but it's not completed, clear contract data
        console.log("Addendum is not completed, clearing contract data");
        setContractData(null);
        setHistoryData(null);
      }
    }
  }, [deal, addendumPreference, displayedAddendumStatus]); // Changed dependency from addendumData to displayedAddendumStatus


  // Helper functions for contract communication (copied from DealDetails.js)
  const getOwnerFullName = (owner) => {
    // Try different possible field names
    const firstName = owner.firstName || owner.first_name || '';
    const middleName = owner.middleName || owner.middle_name || '';
    const lastName = owner.lastName || owner.last_name || '';
    
    const fullName = [firstName, middleName, lastName]
      .filter(name => name && name.trim() !== '')
      .join(' ');
    
    return fullName || 'Unknown Owner';
  };

  const getPrimaryEmailContact = (owner) => {
    // Try different possible structures for contacts
    let contacts = owner.contacts || owner.contacs || []; // Note: "contacs" might be a typo in API
    
    if (!Array.isArray(contacts)) {
      return null;
    }

    return contacts.find(contact => 
      contact.isPrimary === true && 
      contact.deleted === false && 
      contact.type === 'EMAIL'
    );
  };

  const getPrimaryPhoneContact = (owner) => {
    // Try different possible structures for contacts
    let contacts = owner.contacts || owner.contacs || []; // Note: "contacs" might be a typo in API
    
    if (!Array.isArray(contacts)) {
      return null;
    }

    return contacts.find(contact => 
      contact.isPrimary === true && 
      contact.deleted === false && 
      contact.type === 'PHONE'
    );
  };



  //view
  return (
    <Row>
      <Col>
        {/* Loading state for addendum preference */}
        {addendumPreferenceLoading && (
          <Card className="shadow-sm mb-3">
            <CardBody className="text-center p-4">
              <Spinner color="primary" />
              <p className="mt-2 mb-0">Loading contract configuration...</p>
            </CardBody>
          </Card>
        )}

        {/* Error state for addendum preference */}
        {addendumPreferenceError && (
          <Card className="shadow-sm mb-3">
            <CardBody className="p-4">
              <Alert color="danger">
                <i className="ni ni-fat-remove mr-2" />
                Error loading contract configuration: {addendumPreferenceError}
                <Button
                  color="outline-primary"
                  size="sm"
                  className="ml-2"
                  onClick={() => getAddendumPreference(deal)}
                >
                  Retry
                </Button>
              </Alert>
            </CardBody>
          </Card>
        )}

        {/* Show addendum sections only if user wants addendum */}
        {(() => {
          const hasPreference = !!addendumPreference;
          const wantsAddendum = userWantsAddendum();
          const shouldShowAddendum = hasPreference && wantsAddendum;
          
          console.log('🔍 Addendum Section Debug:', {
            addendumPreference,
            hasPreference,
            wantsAddendum,
            shouldShowAddendum,
            addendumData: !!addendumData,
            addendumStatus: addendumData?.status
          });
          
          return shouldShowAddendum;
        })() && (
          <>
            {/* Accordion 1: Current Addendum */}
            <Card className="shadow-sm mb-3">
              <CardHeader
                className="d-flex justify-content-between align-items-center cursor-pointer"
                onClick={() => toggleAccordion("currentAddendum")}
                style={{
                  cursor: "pointer",
                  backgroundColor: accordionOpen.currentAddendum
                    ? "#f8f9fa"
                    : "#fff",
                }}
              >
                <div className="d-flex align-items-center">
                  <i className="ni ni-paper-diploma text-primary mr-2" />
                  <h5 className="mb-0">Current Addendum</h5>
                  {/* {addendumData && (
                    <span className="badge badge-success ml-2">Data Loaded</span>
                  )} */}
                </div>
                <Button color="link" size="sm" className="p-0">
                  <i
                    className={`ni ${
                      accordionOpen.currentAddendum
                        ? "ni-fat-remove"
                        : "ni-fat-add"
                    }`}
                    style={{ fontSize: "1.0rem" }}
                  />
                </Button>
              </CardHeader>
              <Collapse isOpen={accordionOpen.currentAddendum}>
                <CardBody className="p-4">
                  {/* Addendum Loading State */}
                  {addendumLoading && (
                    <div className="text-center">
                      <Spinner color="primary" />
                      <p className="mt-2 mb-0">Loading addendum data...</p>
                    </div>
                  )}

                  {/* Addendum Error State */}
                  {addendumError && (
                    <Alert color="danger">
                      <i className="ni ni-fat-remove mr-2" />
                      Error loading addendum: {addendumError}
                      <Button
                        color="outline-primary"
                        size="sm"
                        className="ml-2"
                        onClick={() => getAddendum(deal)}
                      >
                        Retry
                      </Button>
                    </Alert>
                  )}

                  {/* Addendum Data */}
                  {!addendumLoading && !addendumError && addendumData && (
                    <>
                      <Document
                        documentEvent={addendumData}
                        file={null}
                        title="Current Addendum Status"
                        alwaysExpanded={true}
                        historyData={addendumHistoryData}
                        deal={deal}
                        documentType="ADDENDUM"
                      />
                      
                    </>
                  )}

                  {/* No Addendum Data */}
                  {!addendumLoading && !addendumError && !addendumData && (
                    <div className="text-center text-muted py-4">
                      <i
                        className="ni ni-paper-diploma"
                        style={{ fontSize: "2rem" }}
                      />
                      <p className="mt-2 mb-0">No addendum data available</p>
                    </div>
                  )}
                </CardBody>
              </Collapse>
            </Card>

            {/* Accordion 2: Addendum History */}
            <Card className="shadow-sm mb-3">
              <CardHeader
                className="d-flex justify-content-between align-items-center cursor-pointer"
                onClick={() => toggleAccordion("addendumHistory")}
                style={{
                  cursor: "pointer",
                  backgroundColor: accordionOpen.addendumHistory
                    ? "#f8f9fa"
                    : "#fff",
                }}
              >
                <div className="d-flex align-items-center">
                  <i className="ni ni-folder-17 text-primary mr-2" />
                  <h5 className="mb-0">Addendum History</h5>
                </div>
                <div className="d-flex align-items-center">
                  {/* History Loading Button */}
                  {addendumData?.envelopeId && (
                    <Button
                      color="outline-info"
                      size="sm"
                      className="mr-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Refresh both history and document data
                        getHistory(addendumData.envelopeId, "addendum");
                        getAddendum(deal);
                      }}
                      disabled={addendumHistoryLoading || addendumLoading}
                    >
                      {addendumHistoryLoading || addendumLoading ? (
                        <Spinner size="sm" />
                      ) : (
                        <>
                          <i className="ni ni-sound-wave mr-1" />
                          Load History
                        </>
                      )}
                    </Button>
                  )}
                  <Button color="link" size="sm" className="p-0">
                    <i
                      className={`ni ${
                        accordionOpen.addendumHistory
                          ? "ni-fat-remove"
                          : "ni-fat-add"
                      }`}
                      style={{ fontSize: "1.0rem" }}
                    />
                  </Button>
                </div>
              </CardHeader>
              <Collapse isOpen={accordionOpen.addendumHistory}>
                <CardBody className="p-4">
                  {/* Addendum History Loading State */}
                  {addendumHistoryLoading && (
                    <div className="text-center">
                      <Spinner color="primary" />
                      <p className="mt-2 mb-0">Loading addendum history...</p>
                    </div>
                  )}

                  {/* Addendum History Error State */}
                  {addendumHistoryError && (
                    <Alert color="danger">
                      <i className="ni ni-fat-remove mr-2" />
                      Error loading addendum history: {addendumHistoryError}
                      <Button
                        color="outline-primary"
                        size="sm"
                        className="ml-2"
                        onClick={() =>
                          addendumData?.envelopeId &&
                          getHistory(addendumData.envelopeId, "addendum")
                        }
                      >
                        Retry
                      </Button>
                    </Alert>
                  )}

                  {/* Addendum History Table */}
                  {addendumHistoryData && (Array.isArray(addendumHistoryData) ? addendumHistoryData.length > 0 : addendumHistoryData.activities) && (
                    <DocumentHistoryTable
                      documentHistory={addendumHistoryData}
                      title="Addendum History"
                      documentType="ADDENDUM"
                    />
                  )}

                  {/* No Addendum History Data */}
                  {!addendumHistoryLoading &&
                    !addendumHistoryError &&
                    (!addendumHistoryData || (Array.isArray(addendumHistoryData) && addendumHistoryData.length === 0)) && (
                      <DocumentHistoryTable
                        documentHistory={[]}
                        title="Addendum History"
                        documentType="ADDENDUM"
                      />
                    )}
                </CardBody>
              </Collapse>
            </Card>
          </>
        )}

        {/* Show contract sections when preference is loaded */}
        {addendumPreference && (
          <>
            {/* Accordion 3: Current Contract */}
            <Card
              className={`shadow-sm mb-3 ${
                !isAddendumCompleted() ? "border-muted" : ""
              }`}
            >
              <CardHeader
                className={`d-flex justify-content-between align-items-center ${
                  isAddendumCompleted() ? "cursor-pointer" : ""
                }`}
                onClick={() => toggleAccordion("currentContract")}
                style={{
                  cursor: isAddendumCompleted() ? "pointer" : "not-allowed",
                  backgroundColor: accordionOpen.currentContract
                    ? "#f8f9fa"
                    : "#fff",
                  opacity: isAddendumCompleted() ? 1 : 0.6,
                }}
                title={
                  !isAddendumCompleted() && userWantsAddendum()
                    ? "Contract is only available after addendum is completed"
                    : ""
                }
              >
                <div className="d-flex align-items-center">
                  <i
                    className={`ni ni-paper-diploma ${
                      isAddendumCompleted() ? "text-primary" : "text-muted"
                    } mr-2`}
                  />
                  <h5
                    className={`mb-0 ${
                      !isAddendumCompleted() && userWantsAddendum()
                        ? "text-muted"
                        : ""
                    }`}
                  >
                    Current Contract
                  </h5>
                  {!isAddendumCompleted() && userWantsAddendum() && (
                    <span className="badge badge-warning ml-2">
                      Requires Completed Addendum
                    </span>
                  )}
                </div>
                <Button
                  color="link"
                  size="sm"
                  className="p-0"
                  disabled={!isAddendumCompleted()}
                >
                  <i
                    className={`ni ${
                      accordionOpen.currentContract
                        ? "ni-fat-remove"
                        : "ni-fat-add"
                    } ${
                      !isAddendumCompleted() && userWantsAddendum()
                        ? "text-muted"
                        : ""
                    }`}
                    style={{ fontSize: "1.0rem" }}
                  />
                </Button>
              </CardHeader>
              <Collapse isOpen={accordionOpen.currentContract}>
                <CardBody className="p-4">
                  {/* Addendum Not Completed Message */}
                  {!isAddendumCompleted() && userWantsAddendum() && (
                    <Alert color="info">
                      <i className="ni ni-info-circle mr-2" />
                      Contract information will be available once the addendum
                      is completed.
                    </Alert>
                  )}

                  {/* Contract Loading State */}
                  {isAddendumCompleted() && loading && (
                    <div className="text-center">
                      <Spinner color="primary" />
                      <p className="mt-2 mb-0">Loading contract data...</p>
                    </div>
                  )}

                  {/* Contract Error State */}
                  {isAddendumCompleted() && error && (
                    <Alert color="danger">
                      <i className="ni ni-fat-remove mr-2" />
                      Error loading contract: {error}
                      <Button
                        color="outline-primary"
                        size="sm"
                        className="ml-2"
                        onClick={() => getContract(deal)}
                      >
                        Retry
                      </Button>
                    </Alert>
                  )}

                  {/* Contract Data */}
                  {isAddendumCompleted() &&
                    !loading &&
                    !error &&
                    contractData && (
                      <Document
                        documentEvent={contractData}
                        file={null}
                        title="Current Contract Status"
                        alwaysExpanded={true}
                        historyData={historyData}
                        deal={deal}
                        documentType="CONTRACT"
                      />
                    )}

                  {/* No Contract Data */}
                  {isAddendumCompleted() &&
                    !loading &&
                    !error &&
                    !contractData && (
                      <div className="text-center text-muted py-4">
                        <i
                          className="ni ni-paper-diploma"
                          style={{ fontSize: "2rem" }}
                        />
                        <p className="mt-2 mb-0">No contract data available</p>
                      </div>
                    )}
                </CardBody>
              </Collapse>
            </Card>

            {/* Accordion 4: Contract History */}
            <Card
              className={`shadow-sm mb-3 ${
                !isAddendumCompleted() ? "border-muted" : ""
              }`}
            >
              <CardHeader
                className={`d-flex justify-content-between align-items-center ${
                  isAddendumCompleted() ? "cursor-pointer" : ""
                }`}
                onClick={() => toggleAccordion("contractHistory")}
                style={{
                  cursor: isAddendumCompleted() ? "pointer" : "not-allowed",
                  backgroundColor: accordionOpen.contractHistory
                    ? "#f8f9fa"
                    : "#fff",
                  opacity: isAddendumCompleted() ? 1 : 0.6,
                }}
                title={
                  !isAddendumCompleted() && userWantsAddendum()
                    ? "Contract history is only available after addendum is completed"
                    : ""
                }
              >
                <div className="d-flex align-items-center">
                  <i
                    className={`ni ni-folder-17 ${
                      isAddendumCompleted() ? "text-primary" : "text-muted"
                    } mr-2`}
                  />
                  <h5
                    className={`mb-0 ${
                      !isAddendumCompleted() && userWantsAddendum()
                        ? "text-muted"
                        : ""
                    }`}
                  >
                    Contract History
                  </h5>
                  {!isAddendumCompleted() && userWantsAddendum() && (
                    <span className="badge badge-warning ml-2">
                      Requires Completed Addendum
                    </span>
                  )}
                </div>
                <div className="d-flex align-items-center">
                  {/* History Loading Button */}
                  {contractData?.envelopeId && isAddendumCompleted() && (
                    <Button
                      color="outline-info"
                      size="sm"
                      className="mr-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Refresh both history and document data
                        getHistory(contractData.envelopeId, "contract");
                        getContract(deal);
                      }}
                      disabled={historyLoading || loading}
                    >
                      {historyLoading || loading ? (
                        <Spinner size="sm" />
                      ) : (
                        <>
                          <i className="ni ni-sound-wave mr-1" />
                          Load History
                        </>
                      )}
                    </Button>
                  )}
                  <Button
                    color="link"
                    size="sm"
                    className="p-0"
                    disabled={!isAddendumCompleted()}
                  >
                    <i
                      className={`ni ${
                        accordionOpen.contractHistory
                          ? "ni-fat-remove"
                          : "ni-fat-add"
                      } ${
                        !isAddendumCompleted() && userWantsAddendum()
                          ? "text-muted"
                          : ""
                      }`}
                      style={{ fontSize: "1.0rem" }}
                    />
                  </Button>
                </div>
              </CardHeader>
              <Collapse isOpen={accordionOpen.contractHistory}>
                <CardBody className="p-4">
                  {/* Addendum Not Completed Message */}
                  {!isAddendumCompleted() && userWantsAddendum() && (
                    <Alert color="info">
                      <i className="ni ni-info-circle mr-2" />
                      Contract history will be available once the addendum is
                      completed.
                    </Alert>
                  )}

                  {/* History Loading State */}
                  {isAddendumCompleted() && historyLoading && (
                    <div className="text-center">
                      <Spinner color="primary" />
                      <p className="mt-2 mb-0">Loading contract history...</p>
                    </div>
                  )}

                  {/* History Error State */}
                  {isAddendumCompleted() && historyError && (
                    <Alert color="danger">
                      <i className="ni ni-fat-remove mr-2" />
                      Error loading history: {historyError}
                      <Button
                        color="outline-primary"
                        size="sm"
                        className="ml-2"
                        onClick={() =>
                          contractData?.envelopeId &&
                          getHistory(contractData.envelopeId)
                        }
                      >
                        Retry
                      </Button>
                    </Alert>
                  )}

                  {/* Contract History Table */}
                  {isAddendumCompleted() &&
                    historyData && (Array.isArray(historyData) ? historyData.length > 0 : historyData.activities) && (
                      <DocumentHistoryTable
                        documentHistory={historyData}
                        title="Contract History"
                        documentType="CONTRACT"
                      />
                    )}

                  {/* No History Data */}
                  {isAddendumCompleted() &&
                    !historyLoading &&
                    !historyError &&
                    (!historyData || (Array.isArray(historyData) && historyData.length === 0)) && (
                      <DocumentHistoryTable
                        documentHistory={[]}
                        title="Contract History"
                        documentType="CONTRACT"
                      />
                    )}
                </CardBody>
              </Collapse>
            </Card>
          </>
        )}
      </Col>

      {/* Success Toast */}
      <div
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
        }}
      >
        <Toast isOpen={showSuccessToast}>
          <ToastHeader
            icon="success"
            toggle={() => setShowSuccessToast(false)}
          >
            Success
          </ToastHeader>
          <ToastBody>
            {toastMessage}
          </ToastBody>
        </Toast>
      </div>
    </Row>
  );
};

//TODO: View current contract

//TODO: View contract history
export default ContractTab;
